// Il modello a cascata (tela, artboard 41, 42 e 43): prima il provider, poi un modello del suo
// catalogo, poi il livello di ragionamento fra quelli che quel modello dichiara. Serve al pop, al
// «Predefinito di PopTown» nella pagina Provider e all'attività, dove la prima voce è il pop. Niente è scritto qui: provider, modelli e livelli
// arrivano dal server, che li rilegge dai provider.
import { Field } from '../../components/Field';
import { Pop } from '../../components/Pop';
import { Icon } from '../../components/Icon';
import { Select } from '../../components/Select';
import { Switch } from '../../components/Switch';
import type { CatalogModel, ConnectedProvider, DefaultChoice } from '../../lib/api';

/** Come il server scrive un modello: il nome nudo per il locale, per i CLI con l'account davanti
 * (`codex:gpt-6.1-sol` per il primo account del tipo, `codex@<id>:gpt-6.1-sol` per gli altri). */
export function modelKey(p: ConnectedProvider, m: CatalogModel | string): string {
  const id = typeof m === 'string' ? m : m.id;
  return p.kind === 'local' ? id : `${p.ref ?? p.kind}:${id}`;
}

export function locate(
  providers: ConnectedProvider[],
  model: string | null,
): { provider: ConnectedProvider; entry: CatalogModel } | null {
  if (!model) return null;
  for (const p of providers) {
    const entry = p.catalog.find((m) => modelKey(p, m) === model);
    if (entry) return { provider: p, entry };
  }
  return null;
}

const FRAME = {
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-lg)',
  background: 'var(--panel-2)',
  padding: '14px 16px',
  display: 'grid',
  gap: 12,
} as const;

export type ModelValue = { model: string | null; effort: string | null; thinking: boolean };

export function ModelPicker({
  providers,
  value,
  onChange,
  title = 'Modello',
  subtitle = 'prima il provider, poi il modello e quanto ragiona',
  follows,
  followsName = 'Predefinito',
  followsSource = 'il predefinito di PopTown',
  followsFrom = 'dal predefinito',
  hero,
}: {
  providers: ConnectedProvider[];
  value: ModelValue;
  onChange: (v: ModelValue) => void;
  title?: string;
  subtitle?: string;
  /** il predefinito di PopTown: se c'è, la prima voce del provider è «Predefinito» e lo segue */
  follows?: { choice: DefaultChoice; resolved: string | null };
  /** il nome della prima voce e da chi viene il modello (l'attività segue il suo pop) */
  followsName?: string;
  followsSource?: string;
  followsFrom?: string;
  /** la fascia gialla con un pop (tela, Provider: «Predefinito di PopTown») */
  hero?: { avatar: string };
}) {
  const { model, effort, thinking } = value;
  const found = locate(providers, model);
  const provider = found?.provider ?? null;
  const entry = found?.entry ?? null;
  const following = !!follows && !model;
  const fallback = follows ? locate(providers, follows.resolved) : null;
  const fallbackLabel = fallback
    ? `${fallback.provider.name} · ${fallback.entry.label}`
    : (follows?.resolved ?? 'nessun provider');

  const pickProvider = (id: string) => {
    const p = providers.find((x) => x.id === id);
    const first = p?.catalog[0];
    onChange({ model: p && first ? modelKey(p, first) : null, effort: null, thinking });
  };

  const levels = entry?.efforts ?? [];
  let note: string;
  if (following)
    note = `Segue ${followsSource}: oggi ${fallbackLabel}${follows?.choice.effort ? ', ragionamento ' + follows.choice.effort : ''}.`;
  else if (levels.length)
    note = `Livelli di questo modello: ${levels.join(', ')}. Modelli e livelli arrivano dal catalogo del provider e si aggiornano da soli.`;
  else if (entry) note = 'Senza livelli dichiarati il ragionamento si accende o si spegne.';
  else note = 'Scegli un provider fra quelli collegati.';

  return (
    <section className={hero ? 'model-hero' : undefined} style={hero ? undefined : FRAME}>
      {hero ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Pop avatar={hero.avatar} size={52} />
          <div>
            <h2 className="model-hero-title">{title}</h2>
            <div className="model-hero-sub">{subtitle}</div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Icon name="cpu" size={16} />
          <span className="t-strong">{title}</span>
          <span className="t-caption dim">· {subtitle}</span>
        </div>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 12,
          alignItems: 'start',
        }}
      >
        <Field label="Provider">
          <Select
            label="Provider"
            value={provider?.id ?? ''}
            onChange={pickProvider}
            width="100%"
            options={[
              ...(follows
                ? [{ value: '', label: `${followsName} · ${fallbackLabel}` }]
                : provider
                  ? []
                  : [{ value: '', label: 'Scegli il provider', disabled: true }]),
              ...providers.map((p) => ({ value: p.id, label: p.name })),
            ]}
          />
        </Field>
        <Field label="Modello">
          <Select
            label="Modello"
            value={following ? '' : (model ?? '')}
            onChange={(v) => onChange({ model: v || null, effort: null, thinking })}
            width="100%"
            options={
              following
                ? [
                    {
                      value: '',
                      label: `${fallback?.entry.label ?? fallbackLabel} (${followsFrom})`,
                    },
                  ]
                : provider
                  ? provider.catalog.map((m) => ({
                      value: modelKey(provider, m),
                      label: m.label === m.id ? m.id : `${m.label} · ${m.id}`,
                    }))
                  : [{ value: '', label: 'Scegli prima il provider', disabled: true }]
            }
          />
        </Field>
        {!following && levels.length > 0 && entry && (
          <Field label="Ragionamento">
            <Select
              label="Ragionamento"
              value={effort ?? ''}
              onChange={(v) => onChange({ model, effort: v || null, thinking })}
              width="100%"
              options={[
                {
                  value: '',
                  label: `Quello del modello${entry.defaultEffort ? ' · ' + entry.defaultEffort : ''}`,
                },
                ...levels.map((e) => ({ value: e, label: e })),
              ]}
            />
          </Field>
        )}
        {!following && levels.length === 0 && (
          <Field label="Ragionamento">
            <div
              style={{
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-md)',
                padding: '7px 12px',
                background: 'var(--bg)',
              }}
            >
              <Switch
                checked={thinking}
                onChange={(v) => onChange({ model, effort: null, thinking: v })}
                label="Ragiona prima di rispondere"
              />
            </div>
          </Field>
        )}
      </div>
      <div className="t-caption dim">{note}</div>
    </section>
  );
}
